import { parseSearchFiltersToUrl } from '~/apps/pu-links/infrastructure/searchUrlHelpers'
import type { SearchFilters as UrlSearchFilters } from '~/apps/search/schemas'
import { getTransformObject } from '~/common/photos/transforms'
import { useGtmLogRequest, type Logger } from '~/composables/useGtmLogRequest'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { keysToCamel } from '~/utility/toCamel'
import type {
  CampsiteMapPoint,
  MapSearchCampsitesResults,
  SearchFilters,
} from '../schemas'
import { campsiteMapPopupSchema } from '../schemas'
import { formatBounds } from './bounds'

/** Money object as returned by the API (snake_case) */
interface ApiMoney {
  amount: number
  amount_minor: number
  currency: string
}

/** Raw API response from /search2/postgres-nonprice/ (snake_case) */
interface NonPriceSearchResponse {
  results: NonPriceCampsiteRaw[]
  page: number
  num_pages: number
  count: number
  bounding_box?: [number, number][]
  title?: string
  heading?: string
  bookable?: number
  bookable_count?: number
  full_bookable_count?: number
  category?: unknown
  path?: string
  point?: { lat: number; lng: number }
  lead_price?: [ApiMoney, number | null]
  cheapest_lead_price?: [ApiMoney, number | null, number | null]
  sort_options: string[]
  sort?: string
  sort_field?: string
  max_price?: number
  country?: string
  within?: { mi: number; km: number }
}

/** Raw campsite from non-price endpoint (snake_case, before keysToCamel) */
interface NonPriceCampsiteRaw {
  id: number
  slug: string
  point: { lat: number; lng: number }
  cheapest_lead_price?: [ApiMoney, number | null, number | null] | null
  lead_price?: ApiMoney | null
}

/** Raw campsite from non-price endpoint after keysToCamel */
interface NonPriceCampsiteCamel {
  id: number
  slug: string
  point: { lat: number; lng: number }
  cheapestLeadPrice?:
    | [
        { amount: number; amountMinor: number; currency: string },
        number | null,
        number | null,
      ]
    | null
  leadPrice?: { amount: number; amountMinor: number; currency: string } | null
}

/** Raw API response from /_/campsite-details/slim/ (camelCase from API) */
interface CampsiteSlimResponse {
  _id?: string
  id: number
  campsite_id?: number
  name: string
  slug: string
  path: string
  point: string // WKT format: "SRID=4326;POINT (lng lat)"
  isBookable: boolean
  hierarchyTextShort: string
  rateCount: number
  rating10: number
  categories?: { id: string; name: string; slug: string }[]
  primaryPhoto?: {
    url: { master_image: string }
    caption?: string
  } | null
  leadPrice?: { amount: number; amountMinor: number; currency: string }
  leadPriceNights?: number | null
  leadPricePersonsIncluded?: number | null
}

export class FetchCampsiteMapSearchService {
  async search(
    filters: SearchFilters,
    langCode: string,
    _logger?: Logger,
    fetchOptions?: Record<string, unknown>,
  ): Promise<MapSearchCampsitesResults | undefined> {
    const params = this.filtersToParams(filters)
    const fieldsToShow = [
      'cheapest_lead_price',
      'id',
      'lead_price',
      'point',
      'slug',
    ]
    const url = getLangFallbackUrl(
      langCode,
      `/search2/postgres-nonprice/?fields=${fieldsToShow.join(
        ',',
      )}&sort=magic&facet_counts=none&seo=0&${params.toString()}`,
    )

    const response = await $fetch<NonPriceSearchResponse>(url, fetchOptions)
    if (!response) {
      return
    }
    return {
      campsites: this.formatResults(
        keysToCamel(response.results) as NonPriceCampsiteCamel[],
      ),
      pagination: {
        currentPage: Number(response.page),
        totalPages: response.num_pages,
        totalCount: response.count,
      },
      bounds: response.bounding_box
        ? formatBounds(
            response.bounding_box as [[number, number], [number, number]],
          )
        : null,
      title: response.title,
      heading: response.heading,
      bookable: !!response.bookable,
      category: response.category,
      count: response.bookable_count ?? response.count,
      path: response.path,
      point: response.point,
      leadPrice: response.lead_price,
      cheapestLeadPrice: response.cheapest_lead_price
        ? [
            {
              amount: response.cheapest_lead_price[0].amount,
              amountMinor: response.cheapest_lead_price[0].amount_minor,
              currency: response.cheapest_lead_price[0].currency,
            },
            response.cheapest_lead_price[1],
            response.cheapest_lead_price[2],
          ]
        : undefined,
      sortOptions: response.sort_options,
      sortOption: response.sort || response.sort_field || '',
      maxPrice: response.max_price,
      within: response.within,
    }
  }

  private formatResults(results: NonPriceCampsiteCamel[]): CampsiteMapPoint[] {
    return results.map((campsite) => {
      const { lat, lng } = campsite.point
      return {
        id: campsite.id.toString(),
        slug: campsite.slug,
        price: campsite.cheapestLeadPrice
          ? campsite.cheapestLeadPrice[0]
          : undefined,
        point: { lat, lng },
        lat,
        lng,
      }
    })
  }

  private filtersToParams(filters: SearchFilters) {
    const params = new URLSearchParams([])
    for (const param of parseSearchFiltersToUrl(filters as UrlSearchFilters)) {
      params.append(param.filterName, param.filterValue)
    }
    if (params.get('bounds')) {
      if (params.get('path')) {
        params.delete('bounds')
      }
    }
    params.append('bookable', '1')
    return params
  }

  async fetchCampsiteBySlug(slug: string, langCode: string, logger: Logger) {
    const gtmLogRequest = useGtmLogRequest(
      'FetchCampsiteMapSearchService-fetchCampsiteBySlug',
      logger,
    )
    gtmLogRequest.markStart()

    const campsite = await $fetch<CampsiteSlimResponse>(
      getLangFallbackUrl(langCode, `/_/campsite-details/slim/${slug}/`),
    )
    gtmLogRequest.markEnd()
    return campsiteMapPopupSchema.parse({
      ...campsite,
      bookable: campsite.isBookable,
      hierarchy: campsite.hierarchyTextShort,
      photo: campsite.primaryPhoto
        ? {
            url: getTransformObject(
              'map',
              campsite.primaryPhoto.url.master_image,
            ),
            caption: campsite.primaryPhoto.caption ?? '',
          }
        : null,
      price: campsite.leadPrice ? campsite.leadPrice : undefined,
      status: campsite.isBookable ? 'bookable' : 'free',
    })
  }
}

export function getCampsiteBySlug(
  slug: string,
  langCode: string,
  logger: Logger,
) {
  const service = new FetchCampsiteMapSearchService()
  return service.fetchCampsiteBySlug(slug, langCode, logger)
}
